import React, { Fragment } from 'react';
import { fallbackValues } from './PaymentDetails.theme.js';
import { noop } from 'rxjs';
import {
  Box,
  Stack,
  SolidDivider,
  ButtonWithAction,
  Cluster,
  Title,
  Text,
  LabeledAmount,
  CollapsibleSection,
  Motion,
  util,
  constants,
} from '@thecb/components';
import LineItemGrid from '../line-item-grid';
import { useSelector, useDispatch } from 'react-redux';
import { toggleExpanded } from './PaymentDetails.state.js';
import { formatWithParentheses } from '../../utils/helpers';

const {
  theme: { themeComponent },
} = util;

const PaymentDetailsContent = ({
  lineItems,
  feeElems,
  subtotal,
  themeValues,
  hasVoidablePaymentsSection,
  voidableTransactionDetails,
  voidableAmountPaid,
  partialVoidAction,
  serviceFees,
  convenienceFees,
}) => {
  const amountPaid = [voidableAmountPaid, serviceFees, convenienceFees]
    .filter(Boolean)
    .reduce((total, fee) => total + fee, 0);

  return (
    <Stack childGap="16px">
      <LineItemGrid lineItems={lineItems} />
      {hasVoidablePaymentsSection && (
        <Box background={constants.colors.ATHENS_GREY}>
          <Text
            variant="p"
            color={themeValues.text}
            weight="400"
            fontSize="1rem"
          >
            Paid
          </Text>
          {voidableTransactionDetails.map((t) => (
            <Cluster key={t.id} justify="space-between" align="center">
              <Cluster
                padding="0"
                align="center"
                extraStyles={`font-size: 14px;`}
              >
                <Box padding="0">{t.voidText}</Box>
                <ButtonWithAction
                  variant="ghost"
                  action={() => partialVoidAction(t)}
                  text="Void"
                  padding="0"
                  extraStyles={`
                    min-width: 65px; 
                    margin: 0px;
                    min-height: 0px;
                  `}
                  textExtraStyles={`font-weight: ${constants.fontWeights.FONT_WEIGHT_REGULAR}; font-size: 1rem;`}
                />
              </Cluster>
              <Box padding="0" key={t.id}>
                {util.general.displayCurrency(t.amount_processed)}
              </Box>
            </Cluster>
          ))}
          <Box padding="16px 0px">
            <SolidDivider />
          </Box>
          {!!serviceFees && (
            <Box id="service-fees" padding="0">
              <LabeledAmount
                extraStyles={`font-weight: ${constants.fontWeights.FONT_WEIGHT_REGULAR}; font-size: 14px;`}
                label="Service Fees"
                amount={util.general.displayCurrency(serviceFees)}
              />
              <Box padding="16px 0px">
                <SolidDivider />
              </Box>
            </Box>
          )}
          {!!convenienceFees && (
            <Box id="convenience-fees" padding="0">
              <LabeledAmount
                extraStyles={`font-weight: ${constants.fontWeights.FONT_WEIGHT_REGULAR}; font-size: 14px;`}
                label="Transaction Fee"
                amount={util.general.displayCurrency(convenienceFees)}
              />
              <Box padding="16px 0px">
                <SolidDivider />
              </Box>
            </Box>
          )}
          <LabeledAmount
            extraStyles={`font-weight: ${constants.fontWeights.FONT_WEIGHT_REGULAR}; font-size: 14px;`}
            label="Amount Paid"
            amount={util.general.displayCurrency(amountPaid)}
          />
        </Box>
      )}
      {!hasVoidablePaymentsSection && !!lineItems.length && (
        <SolidDivider /> // avoids duplicate dividers -> case of voidable payment section, that acts as divider
      )}
      {subtotal ? (
        <>
          <Box padding="0.5rem 0">
            <LabeledAmount
              variant={themeValues.labeledAmountSubtotal}
              label="Subtotal"
              amount={util.general.displayCurrency(subtotal)}
            />
            {feeElems}
          </Box>
          <SolidDivider />
        </>
      ) : (
        <></>
      )}
    </Stack>
  );
};

const Collapsible = ({
  content,
  title,
  supportsTouch,
  isExpanded,
  dispatch,
}) => (
  <CollapsibleSection
    isMobile={true}
    supportsTouch={supportsTouch}
    isOpen={isExpanded}
    title={title}
    toggleSection={() => dispatch(toggleExpanded())}
    customTitle={true}
    name="Collapsed Payment Details"
  >
    <Motion
      variants={{
        open: { opacity: 1 },
        closed: { opacity: 0 },
      }}
      positionTransition
      initial={false}
    >
      {content}
    </Motion>
  </CollapsibleSection>
);

const NonCollapsible = ({ title, content }) => (
  <Stack>
    {title}
    {content}
  </Stack>
);

const BottomSection = ({
  hasVoidablePaymentsSection,
  remainingBalance,
  total,
  isExpanded,
  isCollapsible,
}) => (
  <>
    <>
      {isCollapsible && <Box padding="8px 0" />}
      {(!isCollapsible || !isExpanded) && (
        <>
          <SolidDivider />
          <Box padding="8px 0px" />
        </>
      )}
    </>
    <Cluster
      justify="space-between"
      align="center"
      id="payment-details-bottom-section"
    >
      <Box padding="0 8px 0 0">
        <Text
          fontSize="1.25rem"
          extraStyles={`font-weight: ${constants.fontWeights.FONT_WEIGHT_SEMIBOLD};`}
        >
          {hasVoidablePaymentsSection ? 'Remaining amount due' : 'Total'}
        </Text>
      </Box>
      <Box padding="0 8px 0 0">
        <Text
          fontSize="1.25rem"
          extraStyles={`font-weight: ${constants.fontWeights.FONT_WEIGHT_SEMIBOLD};`}
        >
          {util.general.displayCurrency(
            typeof remainingBalance === 'number' ? remainingBalance : total,
          )}
        </Text>
      </Box>
    </Cluster>
  </>
);

const PaymentDetails = ({
  lineItems: _lineItems,
  fees: _fees,
  subtotal,
  total,
  hideTitle = false,
  titleText = 'Payment Details',
  collapsibleOnMobile = true,
  isMobile,
  supportsTouch,
  themeValues,
  // partial void section (optional)
  hasVoidablePaymentsSection = false,
  voidableTransactionDetails = [],
  partialVoidAction = noop,
  voidableAmountPaid = 0,
  remainingBalance = false,
  // end partial void section
  serviceFees,
  convenienceFees,
}) => {
  const isExpanded = useSelector((state) => state.paymentDetails.isExpanded);
  const dispatch = useDispatch();
  const fees = _fees
    .filter((fee) => fee.amount > 0)
    .map(({ amount, ...rest }) => ({
      ...rest,
      amount: util.general.displayCurrency(amount),
    }));
  const lineItems = _lineItems.map(
    ({ amount, is_negative_amount, ...rest }) => ({
      ...rest,
      amount: is_negative_amount
        ? formatWithParentheses(util.general.displayCurrency(amount))
        : util.general.displayCurrency(amount),
    }),
  );
  const isCollapsible = isMobile && collapsibleOnMobile;
  const feeElems = fees.map((fee) => (
    <Fragment key={fee.label}>
      <Box padding="4px 0" />
      <LabeledAmount
        key={fee.label}
        variant={themeValues.labeledAmountSubtotal}
        {...fee}
      />
    </Fragment>
  ));
  const content = isCollapsible ? (
    <Stack childGap="0">
      <Box padding="4.5px" />
      <SolidDivider />
      <Box padding="8px" />
      <PaymentDetailsContent
        {...{
          lineItems,
          fees,
          subtotal,
          themeValues,
          hasVoidablePaymentsSection,
          voidableTransactionDetails,
          voidableAmountPaid,
          partialVoidAction,
          serviceFees,
          convenienceFees,
        }}
      />
    </Stack>
  ) : (
    <PaymentDetailsContent
      {...{
        lineItems,
        feeElems,
        subtotal,
        themeValues,
        hasVoidablePaymentsSection,
        voidableTransactionDetails,
        voidableAmountPaid,
        partialVoidAction,
        serviceFees,
        convenienceFees,
      }}
    />
  );
  const title = hideTitle ? (
    <Fragment />
  ) : isCollapsible ? (
    <Box width="100%" padding="none">
      <Title
        weight={constants.fontWeights.FONT_WEIGHT_SEMIBOLD}
        as="h1"
        extraStyles={`font-size: 1.375rem;`}
        id="payment-details-title"
      >
        {titleText}
      </Title>
    </Box>
  ) : (
    <Title
      weight={constants.fontWeights.FONT_WEIGHT_SEMIBOLD}
      as="h1"
      extraStyles={`font-size: 1.375rem;`}
      id="payment-details-title"
    >
      {titleText}
    </Title>
  );
  return (
    <>
      {isCollapsible ? (
        <Collapsible
          {...{
            title,
            content,
            isExpanded,
            isMobile,
            supportsTouch,
            dispatch,
          }}
        />
      ) : (
        <NonCollapsible {...{ title, content }} />
      )}
      <BottomSection
        {...{
          hasVoidablePaymentsSection,
          remainingBalance,
          total,
          isExpanded,
          isCollapsible,
        }}
      />
    </>
  );
};

export default themeComponent(
  PaymentDetails,
  'PaymentDetails',
  fallbackValues,
  'default',
);
